import React, { useState, useEffect } from 'react';
import {
  Layout,
  Input,
  Table,
  Space,
  Card,
  Typography,
  Spin,
  Alert,
  Tag,
  FloatButton,
  theme,
  ConfigProvider,
  Button,
  Form,
  Input as AntdInput
} from 'antd';
import { SearchOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;

const App = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);

  const { defaultAlgorithm, darkAlgorithm } = theme;

  // Configuration constants - initially empty, to be filled by user
  const [config, setConfig] = useState({
    serverUrl: '',
    apiKey: '',
    userId: ''
  });

  const [showConfig, setShowConfig] = useState(true); // 默认显示配置，除非通过 URL 参数或 localStorage 预填充
  
  // Format file size from bytes to human readable format
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date to a readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Load configuration from URL parameters or localStorage on component mount
  useEffect(() => {
    // Parse URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlServerUrl = urlParams.get('serverUrl');
    const urlApiKey = urlParams.get('apiKey');
    const urlUserId = urlParams.get('userId');

    // Check if all required parameters are present in URL
    if (urlServerUrl && urlApiKey && urlUserId) {
      const urlConfig = {
        serverUrl: urlServerUrl,
        apiKey: urlApiKey,
        userId: urlUserId
      };
      setConfig(urlConfig);
      setShowConfig(false); // Skip config form if URL parameters are present
    } else {
      // Fallback to localStorage
      const savedConfig = localStorage.getItem('embyConfig');
      if (savedConfig) {
        setConfig(JSON.parse(savedConfig));
        setShowConfig(false);
      }
    }
  }, []);

  // Function to save configuration to localStorage
  const saveConfig = (configData) => {
    localStorage.setItem('embyConfig', JSON.stringify(configData));
    setConfig(configData);
    setShowConfig(false);
  };

  // Function to query Emby API
  const queryMedia = async (searchText) => {
    if (!config.serverUrl || !config.apiKey || !config.userId) {
      setError('请先配置 Emby 服务器信息');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // If no search term is provided, get all items
      let url = `${config.serverUrl}/Users/${config.userId}/Items?IncludeItemTypes=Movie,Series&Recursive=true&Fields=Path,DateCreated,Size&api_key=${config.apiKey}`;
      if (searchText && searchText.trim()) {
        url += `&searchTerm=${encodeURIComponent(searchText)}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Process the results to match our table structure
      const processedItems = data.Items.map(item => ({
        key: item.Id,
        name: item.Name,
        path: item.Path,
        size: item.Size ? formatFileSize(item.Size) : 'N/A',
        dateAdded: formatDate(item.DateCreated),
        type: item.Type,
        id: item.Id
      }));

      setMediaItems(processedItems);
    } catch (err) {
      setError('查询失败: ' + err.message);
      console.error('Error fetching media:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change with debouncing
  const handleSearch = (value) => {
    setSearchTerm(value);
    // Debounce the API call by 300ms
    setTimeout(() => {
      if (value.trim() === '') {
        setMediaItems([]); // Clear results if search term is empty
      } else {
        queryMedia(value);
      }
    }, 300);
  };

  // Define table columns
  const columns = [
    {
      title: '标题',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Space>
          <Text strong style={{ color: darkMode ? '#fff' : 'inherit' }}>{text}</Text>
          <Tag color={record.type === 'Movie' ? 'blue' : 'green'}>
            {record.type === 'Movie' ? '电影' : '剧集'}
          </Tag>
        </Space>
      ),
    },
    {
      title: '路径',
      dataIndex: 'path',
      key: 'path',
      render: (text) => (
        <Text code style={{ fontSize: '12px', color: darkMode ? '#d9d9d9' : 'inherit' }}>
          {text}
        </Text>
      ),
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
      sorter: (a, b) => {
        // Convert file sizes to bytes for proper sorting
        const sizeToBytes = (sizeStr) => {
          if (sizeStr === 'N/A') return 0;
          const [value, unit] = sizeStr.split(' ');
          const numValue = parseFloat(value);
          switch (unit) {
            case 'KB': return numValue * 1024;
            case 'MB': return numValue * 1024 * 1024;
            case 'GB': return numValue * 1024 * 1024 * 1024;
            case 'TB': return numValue * 1024 * 1024 * 1024 * 1024;
            default: return numValue; // Bytes
          }
        };
        return sizeToBytes(a.size) - sizeToBytes(b.size);
      },
    },
    {
      title: '加入日期',
      dataIndex: 'dateAdded',
      key: 'dateAdded',
      sorter: (a, b) => new Date(a.dateAdded) - new Date(b.dateAdded),
    },
  ];

  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState([]);

  // Function to find duplicate media items
  const findDuplicates = async () => {
    if (!config.serverUrl || !config.apiKey || !config.userId) {
      setError('请先配置 Emby 服务器信息');
      return;
    }
    
    // If no media items exist, fetch all items first
    if (mediaItems.length === 0) {
      try {
        const response = await fetch(
          `${config.serverUrl}/Users/${config.userId}/Items?IncludeItemTypes=Movie,Series&Recursive=true&Fields=Path,DateCreated,Size&api_key=${config.apiKey}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Process the results to match our table structure
        const processedItems = data.Items.map(item => ({
          key: item.Id,
          name: item.Name,
          path: item.Path,
          size: item.Size ? formatFileSize(item.Size) : 'N/A',
          dateAdded: formatDate(item.DateCreated),
          type: item.Type,
          id: item.Id
        }));

        setMediaItems(processedItems);

        // Then find duplicates
        findDuplicateItems(processedItems);
      } catch (err) {
        setError('查询失败: ' + err.message);
        console.error('Error fetching media:', err);
      }
    } else {
      // If media items already exist, just find duplicates among them
      findDuplicateItems(mediaItems);
    }
  };

  // Helper function to find duplicate items
  const findDuplicateItems = (items) => {
    // Group items by name to find duplicates
    const groupedItems = {};
    items.forEach(item => {
      if (groupedItems[item.name]) {
        groupedItems[item.name].push(item);
      } else {
        groupedItems[item.name] = [item];
      }
    });

    // Find items that appear more than once
    const duplicateGroups = Object.values(groupedItems).filter(group => group.length > 1);

    // Flatten to get all duplicate items
    const duplicateItems = duplicateGroups.flat();

    setDuplicates(duplicateItems);
    setShowDuplicates(true);
  };

  // Define separate columns for duplicates table
  const duplicateColumns = [
    {
      title: '标题',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Text strong style={{ color: darkMode ? '#fff' : 'inherit' }}>{text}</Text>
          <Tag color={record.type === 'Movie' ? 'blue' : 'green'}>
            {record.type === 'Movie' ? '电影' : '剧集'}
          </Tag>
        </Space>
      ),
    },
    {
      title: '路径',
      dataIndex: 'path',
      key: 'path',
      render: (text) => (
        <Text code style={{ fontSize: '12px', color: darkMode ? '#d9d9d9' : 'inherit' }}>
          {text}
        </Text>
      ),
    },
    {
      title: '大小',
      dataIndex: 'size',
      key: 'size',
    },
    {
      title: '加入日期',
      dataIndex: 'dateAdded',
      key: 'dateAdded',
    },
  ];

  // Configuration Form Component
  const ConfigForm = () => {
    const [form] = Form.useForm();
    const [generatedLink, setGeneratedLink] = useState('');
    const [showLink, setShowLink] = useState(false);
    
    const onFinish = (values) => {
      saveConfig(values);
    };
    
    const generateLink = () => {
      form.validateFields().then(values => {
        const currentUrl = window.location.origin + window.location.pathname;
        const link = `${currentUrl}?serverUrl=${encodeURIComponent(values.serverUrl)}&apiKey=${encodeURIComponent(values.apiKey)}&userId=${encodeURIComponent(values.userId)}`;
        setGeneratedLink(link);
        setShowLink(true);
      }).catch(info => {
        console.log('Validate Failed:', info);
      });
    };

    return (
      <Card
        style={{
          marginBottom: '24px',
          borderRadius: '8px',
          background: darkMode ? '#1d1d1d' : '#fff',
          borderColor: darkMode ? '#303030' : '#f0f0f0',
          transition: 'all 0.3s ease'
        }}
        bodyStyle={{
          padding: '20px',
          background: darkMode ? '#1d1d1d' : '#fff'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Title level={4} style={{ marginBottom: '8px', color: darkMode ? '#fff' : '#333' }}>
            配置 Emby 服务器
          </Title>
          <Text type="secondary" style={{ color: darkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>
            请输入您的 Emby 服务器信息
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={config}
        >
          <Form.Item
            label="服务器地址"
            name="serverUrl"
            rules={[{ required: true, message: '请输入服务器地址' }]}
            initialValue={config.serverUrl || 'http://localhost:3000'}
          >
            <AntdInput placeholder="例如: http://192.168.1.100:8096" />
          </Form.Item>
          
          <Form.Item
            label="API 密钥"
            name="apiKey"
            rules={[{ required: true, message: '请输入 API 密钥' }]}
            initialValue={config.apiKey}
          >
            <AntdInput.Password placeholder="输入您的 Emby API 密钥" />
          </Form.Item>
          
          <Form.Item
            label="用户 ID"
            name="userId"
            rules={[{ required: true, message: '请输入用户 ID' }]}
            initialValue={config.userId}
          >
            <AntdInput placeholder="输入您的 Emby 用户 ID" />
          </Form.Item>
          
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button type="primary" htmlType="submit" style={{ flex: 1, marginRight: 8 }}>
                保存配置并继续
              </Button>
              <Button type="default" onClick={generateLink} style={{ flex: 1 }}>
                生成配置链接
              </Button>
            </Space>
          </Form.Item>
        </Form>
        
        {showLink && (
          <Alert
            message="配置链接已生成"
            description={
              <div>
                <div style={{ marginBottom: '8px' }}>
                  <Text copyable={{ text: generatedLink }}>
                    <Text code style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                      {generatedLink}
                    </Text>
                  </Text>
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  将此链接分享给他人，他们打开后无需重新配置即可直接使用
                </Text>
              </div>
            }
            type="info"
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
      </Card>
    );
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: darkMode ? darkAlgorithm : defaultAlgorithm,
        token: {
          colorBgContainer: darkMode ? '#1d1d1d' : '#ffffff',
          colorText: darkMode ? '#ffffff' : '#000000',
          colorBgLayout: darkMode ? '#141414' : '#f5f5f5',
          colorBorder: darkMode ? '#303030' : '#f0f0f0',
        }
      }}
    >
      <Layout style={{ minHeight: '100vh', background: darkMode ? '#141414' : '#f5f5f5' }}>
        <Content style={{
          padding: '24px 24px',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          background: darkMode ? '#141414' : '#f5f5f5'
        }}>
          {showConfig ? (
            <ConfigForm />
          ) : (
            <>
              <Card
                style={{
                  marginBottom: '24px',
                  borderRadius: '8px',
                  background: darkMode ? '#1d1d1d' : '#fff',
                  borderColor: darkMode ? '#303030' : '#f0f0f0',
                  transition: 'all 0.3s ease'
                }}
                bodyStyle={{
                  padding: '20px',
                  background: darkMode ? '#1d1d1d' : '#fff'
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <Title level={4} style={{ marginBottom: '8px', color: darkMode ? '#fff' : '#333' }}>
                    Emby 媒体浏览器
                  </Title>
                  <Text type="secondary" style={{ color: darkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>
                    输入影片或剧集名称进行实时搜索
                  </Text>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <Search
                    placeholder="输入影片标题..."
                    size="large"
                    prefix={<SearchOutlined />}
                    onChange={(e) => {
                      handleSearch(e.target.value);
                      setShowDuplicates(false); // Reset duplicate view when user types
                    }}
                    loading={loading}
                    allowClear
                    style={{ maxWidth: '600px', flex: 1, minWidth: '200px' }}
                  />
                  <Button
                    type="primary"
                    size="large"
                    onClick={findDuplicates}
                    style={{ borderRadius: '6px' }}
                  >
                    查询重复影片
                  </Button>
                  <Button
                    type="default"
                    size="large"
                    onClick={() => setShowConfig(true)}
                    style={{ borderRadius: '6px' }}
                  >
                    更改配置
                  </Button>
                  <Button
                    type="default"
                    size="large"
                    onClick={() => {
                      if (config.serverUrl && config.apiKey && config.userId) {
                        const currentUrl = window.location.origin + window.location.pathname;
                        const link = `${currentUrl}?serverUrl=${encodeURIComponent(config.serverUrl)}&apiKey=${encodeURIComponent(config.apiKey)}&userId=${encodeURIComponent(config.userId)}`;
                        navigator.clipboard.writeText(link).then(() => {
                          setError('配置链接已复制到剪贴板！');
                          setTimeout(() => setError(null), 3000);
                        });
                      } else {
                        setError('请先配置服务器信息！');
                        setTimeout(() => setError(null), 3000);
                      }
                    }}
                    style={{ borderRadius: '6px' }}
                  >
                    复制配置链接
                  </Button>
                </div>
              </Card>

              {error && (
                <Alert
                  message="错误"
                  description={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}

              {showDuplicates && duplicates.length > 0 && (
                <Card
                  style={{
                    marginBottom: '24px',
                    borderRadius: '8px',
                    background: darkMode ? '#1d1d1d' : '#fff',
                    borderColor: darkMode ? '#303030' : '#f0f0f0'
                  }}
                  title="重复影片列表"
                  extra={
                    <Button
                      type="default"
                      size="small"
                      onClick={() => {
                        setShowDuplicates(false);
                        setDuplicates([]);
                        setMediaItems([]);
                      }}
                      style={{ borderRadius: '4px' }}
                    >
                      关闭
                    </Button>
                  }
                >
                  <Table
                    columns={duplicateColumns}
                    dataSource={duplicates}
                    pagination={{
                      defaultPageSize: 10,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '20', '50'],
                    }}
                    locale={{
                      emptyText: '没有重复的影片'
                    }}
                  />
                </Card>
              )}

              {!showDuplicates && (
                <Card
                  style={{
                    borderRadius: '8px',
                    background: darkMode ? '#1d1d1d' : '#fff',
                    borderColor: darkMode ? '#303030' : '#f0f0f0',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{
                    background: darkMode ? '#1d1d1d' : '#fff'
                  }}
                >
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <Spin size="large" />
                      <Text style={{ marginTop: '16px', display: 'block', color: darkMode ? 'rgba(255, 255, 255, 0.65)' : 'inherit' }}>
                        正在搜索媒体...
                      </Text>
                    </div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={mediaItems}
                      pagination={{
                        defaultPageSize: 15,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        onShowSizeChange: (current, size) => {
                          // Handle page size change if needed
                        }
                      }}
                      scroll={{ x: true }}
                      locale={{
                        emptyText: searchTerm ? '没有找到匹配的媒体' : '请输入关键词开始搜索'
                      }}
                    />
                  )}
                </Card>
              )}
            </>
          )}
        </Content>

        <FloatButton
          icon={darkMode ? <SunOutlined /> : <MoonOutlined />}
          onClick={() => setDarkMode(!darkMode)}
          style={{
            right: 24,
            bottom: 24
          }}
        />
      </Layout>
    </ConfigProvider>
  );
};

export default App;